import { Card } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Checkbox } from 'components/ui/checkbox';
import type { Artist } from 'types';
import { MapPin, Music, Heart, Briefcase, Headphones, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatNumberToAbbreviation } from 'utils/helpers';

interface ArtistCardProps {
  artist: Artist;
  isSelected: boolean;
  isLiked: boolean;
  isSelectionDisabled: boolean;
  isOwnArtist: boolean;
  onToggleSelection: (artistId: string) => void;
  onToggleLike: (artistId: string) => void;
  onSpotifyClick: (artist: Artist) => void;
}

export function ArtistCard({
  artist,
  isSelected,
  isLiked,
  isSelectionDisabled,
  isOwnArtist,
  onToggleSelection,
  onToggleLike,
  onSpotifyClick,
}: ArtistCardProps) {
  const { t } = useTranslation('collab-club');
  return (
    <Card className="p-6 hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
      <div className="text-center space-y-4 flex flex-col h-full">
        <div className="relative">
          <Avatar className="h-20 w-20 mx-auto">
            <AvatarImage src={artist.image || '/placeholder.svg'} alt={artist.name} />
            <AvatarFallback className="text-lg">
              {artist.name
                .split(' ')
                .map((n) => n[0])
                .join('')}
            </AvatarFallback>
          </Avatar>
          <div className="absolute -top-2 -right-2 flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onToggleLike(artist.id.toString());
              }}
              className={`h-8 w-8 p-0 rounded-full -my-1 ${
                isLiked
                  ? 'bg-red-50 text-red-500 hover:bg-red-100'
                  : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
              }`}
            >
              <Heart className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} />
            </Button>
            <div
              className="bg-white rounded-full p-1"
              title={isOwnArtist ? t('own_artist_disabled_hint') : undefined}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onToggleSelection(artist.id.toString())}
                disabled={isSelectionDisabled || isOwnArtist}
                className="h-4 w-4"
              />
            </div>
          </div>
        </div>

        <div className="flex-grow">
          <h3 className="font-bold text-lg text-gray-900">{artist.name}</h3>
          <div className="flex items-center justify-center gap-4 text-sm text-gray-600 mt-2">
            <span className="flex items-center gap-1">
              <Music className="h-3 w-3 shrink-0" />
              {artist.genres?.join(', ') || 'Unknown'}
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3 shrink-0" />
              {artist.country || t('unknown_location')}
            </span>
          </div>
          {artist.skills && artist.skills.length > 0 && (
            <>
              <div className="flex items-center justify-center gap-1 text-sm text-gray-600 mt-2">
                <Briefcase className="h-3 w-3 shrink-0" />
                <span>{artist.skills.join(', ')}</span>
              </div>
            </>
          )}
          {artist.monthlyListeners && (
            <div className="flex items-center justify-center gap-1 text-sm text-gray-600 mt-2">
              <Headphones className="h-3 w-3 shrink-0" />
              <span>
                {formatNumberToAbbreviation(Number(artist.monthlyListeners))}{' '}
                {t('monthly_listeners').toLowerCase()}
              </span>
            </div>
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          className="w-full bg-green-500 hover:bg-green-600 text-white border-green-500 hover:border-green-600 box-border hover:text-white"
          onClick={() => onSpotifyClick(artist)}
          disabled={!artist.spotifyUrl}
        >
          <div className="flex items-center gap-2">
            {t('listen_on_spotify')}
            <ExternalLink className="h-3 w-3 shrink-0" />
          </div>
        </Button>
      </div>
    </Card>
  );
}
